import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { FugueContactForm } from '@/components/FugueContactForm'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Globe, Zap, CreditCard, Users, Music, Palette, Camera, Clock, User, ArrowRight } from 'lucide-react'

const services = [
  {
    title: 'Website Design & Development',
    description:
      'Custom websites that are fast, beautiful, and effective. Designed for classical musicians and arts groups.',
    href: '/services/websites',
    icon: Globe,
  },
  {
    title: 'Graphic Design',
    description:
      'Brand identity, posters, programs, and season campaigns. One-time projects or a monthly design retainer.',
    href: '/services/design',
    icon: Palette,
  },
  {
    title: 'Photography',
    description:
      'Concert coverage, headshots, and season photography days, delivered as edited galleries you own outright.',
    href: '/services/photography',
    icon: Camera,
  },
  {
    title: 'Digital Strategy & Consulting',
    description:
      'Strategy intensives and fractional digital direction: audit, roadmap, and ongoing oversight without full execution.',
    href: '/packages#consulting',
    icon: Zap,
  },
  {
    title: 'Packages & Pricing',
    description: 'Clear, affordable options with payment plans for soloists, ensembles, and organizations.',
    href: '/packages',
    icon: CreditCard,
  },
  {
    title: 'Industries We Serve',
    description: 'See how we support solo artists, educators, nonprofits, festivals, and more.',
    href: '/who-we-help',
    icon: Users,
  },
]

const reasonsToWorkWithUs = [
  {
    title: 'Built for Classical Music',
    description: 'We understand your audience, your needs, and the artistic integrity behind your work.',
    icon: Music,
  },
  {
    title: 'Elegant, User-Friendly Design',
    description: 'We balance beauty and usability so your site feels like an extension of your artistry.',
    icon: Palette,
  },
  {
    title: 'Systems That Save You Time',
    description: 'Our tools simplify your admin: from scheduling lessons to collecting donations.',
    icon: Clock,
  },
  {
    title: 'One Point of Contact',
    description: 'Work directly with a developer and strategist who knows classical music.',
    icon: User,
  },
]

export default function ServicesPage() {
  const breadcrumbs = generateBreadcrumbs('/services', breadcrumbLabels)

  useSEO({
    title: 'Services',
    description:
      'Website design, digital strategy, and automation services for classical music organizations. We build elegant websites and digital tools to help you grow your audience.',
    url: '/services',
    canonical: '/services',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Digital Services for Classical Music Organizations',
      description:
        'Website design, digital strategy, and automation services for classical music organizations including festivals, ensembles, and choirs.',
      url: '/services',
    }),
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const servicesRef = useStaggeredGrid<HTMLDivElement>('.service-card', { stagger: 0.1 })
  const reasonsRef = useStaggeredGrid<HTMLDivElement>('.reason-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="m-0 mx-auto w-full space-y-24">
      {/* Hero */}
      <section className="relative min-h-[90vh] w-full overflow-hidden md:h-[75vh]">
        <img src="/cello.jpg" alt="Cellist performing" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div
          ref={heroRef}
          className="relative z-10 mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-y-6 px-8 text-center text-white"
        >
          <h1 className="text-fluid-h1 tracking-tight">Services for the Classical Music World</h1>
          <p className="text-lg leading-relaxed text-white/80">
            We design elegant websites and digital tools to help you grow your audience, simplify operations, and look
            professional online.
          </p>
          <Link to="/contact">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button size="lg" className="group cursor-pointer px-8">
                Start Your Project
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </motion.div>
          </Link>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-3xl px-4 text-center text-lg">
        <p className="text-muted-foreground leading-relaxed">
          Fugue Lab partners with soloists, teachers, ensembles, and organizations to bring modern design and strategy
          to the classical music industry.
        </p>
      </section>

      {/* Services Grid */}
      <section ref={servicesRef} className="mx-auto grid max-w-5xl grid-cols-1 gap-6 px-4 md:grid-cols-2">
        {services.map((service) => {
          const Icon = service.icon
          return (
            <Link
              key={service.title}
              to={service.href}
              className="service-card group border-border/50 from-primary/5 hover:border-primary/40 hover:bg-primary/10 relative cursor-pointer rounded-2xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-lg"
            >
              <motion.div
                className="bg-primary/10 text-primary mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Icon className="h-6 w-6" />
              </motion.div>
              <h3 className="mb-2 text-lg font-semibold">{service.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{service.description}</p>
              <span className="text-primary mt-4 inline-flex items-center gap-1 text-sm font-medium transition-transform duration-300 group-hover:translate-x-1">
                Learn more <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          )
        })}
      </section>

      {/* Why Work With Us */}
      <section className="mx-auto max-w-5xl space-y-10 px-4">
        <h2 className="text-fluid-h2 text-center">Why Work With Fugue Lab?</h2>
        <div ref={reasonsRef} className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {reasonsToWorkWithUs.map((reason) => {
            const Icon = reason.icon
            return (
              <motion.div
                key={reason.title}
                className="reason-card border-border/50 from-primary/5 hover:border-primary/30 relative rounded-2xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-md"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="bg-primary/10 text-primary mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">{reason.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{reason.description}</p>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaRef} className="mx-auto max-w-3xl space-y-6 px-4 text-center">
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Start?</h2>
          <p className="text-muted-foreground text-lg">
            Whether you&apos;re launching a new site or streamlining your admin, we&apos;d love to hear what you&apos;re
            working on.
          </p>
          <FugueContactForm />
        </div>
      </section>
    </div>
  )
}
